import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { ToastProvider } from './components/Toast';
import AppShell from './pages/AppShell';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import FormBuilderPage from './features/formBuilder/FormBuilderPage';
import AreasPage from './features/areas/AreasPage';
import CustomersPage from './features/customers/CustomersPage';
import TeamPage from './features/team/TeamPage';
import SettingsPage from './features/settings/SettingsPage';
import VisitsPage from './features/visits/VisitsPage';
import JourneyPlansPage from './features/visits/JourneyPlansPage';
import ScheduleReportPage from './features/visits/ScheduleReportPage';
import OrdersPage from './features/orders/OrdersPage';
import ProductsPage from './features/orders/ProductsPage';
import ProductMastersPage from './features/orders/ProductMastersPage';
import PriceListsPage from './features/orders/PriceListsPage';
import VisitReportPage from './features/reports/VisitReportPage';
import VisitDashboardPage from './features/reports/VisitDashboardPage';
import VisitAnalyticsPage from './features/reports/VisitAnalyticsPage';
import OrderReportPage from './features/reports/OrderReportPage';
import OrderLinesReportPage from './features/reports/OrderLinesReportPage';
import RepDashboardPage from './features/reports/RepDashboardPage';
import AssistantPage from './features/assistant/AssistantPage';
import LeaveTypesPage from './features/leave/LeaveTypesPage';
import CalendarPage from './features/leave/CalendarPage';
import LeaveRequestsPage from './features/leave/LeaveRequestsPage';
import PlatformPage from './features/platform/PlatformPage';
import TrackingLivePage from './features/tracking/TrackingLivePage';
import RouteReplayPage from './features/tracking/RouteReplayPage';
import ExpenseDashboardPage from './features/expenses/ExpenseDashboardPage';
import ExpenseClaimsPage from './features/expenses/ExpenseClaimsPage';
import ExpenseCategoriesPage from './features/expenses/ExpenseCategoriesPage';
import DispatchSheetsPage from './features/dispatch/DispatchSheetsPage';
import DispatchReasonsPage from './features/dispatch/DispatchReasonsPage';
import LeadsPage from './features/leads/LeadsPage';
import LeadPipelinePage from './features/leads/LeadPipelinePage';
import LeadDashboardPage from './features/leads/LeadDashboardPage';
import DistributorsPage from './features/distributors/DistributorsPage';
import DistributorAreasPage from './features/distributors/DistributorAreasPage';
import DistributorPortalLayout from './features/distributorPortal/DistributorPortalLayout';
import DistributorDashboard from './features/distributorPortal/DistributorDashboard';
import DistributorOrders from './features/distributorPortal/DistributorOrders';
import DistributorPrimaryOrders from './features/distributorPortal/DistributorPrimaryOrders';
import DistributorRetailers from './features/distributorPortal/DistributorRetailers';
import { homeFor } from './lib/routes';
import type { ReactNode } from 'react';

function Protected({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  // Without this gate a hard refresh flashes the login screen for a beat
  // before /auth/me comes back and puts the user right back.
  if (loading) {
    return (
      <div className="auth-screen">
        <span className="spinner" style={{ color: 'var(--brand)', width: 26, height: 26 }} />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** The tenant console. A platform admin is bounced to their own. */
function TenantOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.role === 'platform_admin') return <Navigate to="/platform" replace />;
  if (user?.role === 'distributor') return <Navigate to="/distributor/dashboard" replace />;
  return <>{children}</>;
}

/** Ours. Anybody with a company is bounced back to theirs. */
function PlatformOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user && user.role !== 'platform_admin') {
    return <Navigate to={homeFor(user.role)} replace />;
  }
  return <>{children}</>;
}

/** Distributor portal. Only distributors may enter. */
function DistributorOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user && user.role !== 'distributor') {
    return <Navigate to={homeFor(user.role)} replace />;
  }
  return <>{children}</>;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={homeFor(user.role)} replace />;
  return <LoginPage />;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? homeFor(user.role) : '/login'} replace />;
}

export default function App() {
  return (
    <ToastProvider>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route
          path="/platform"
          element={(
            <Protected>
              <PlatformOnly>
                <PlatformPage />
              </PlatformOnly>
            </Protected>
          )}
        />

        {/* Distributor portal — its own layout, its own shell */}
        <Route
          element={(
            <Protected>
              <DistributorOnly>
                <DistributorPortalLayout />
              </DistributorOnly>
            </Protected>
          )}
        >
          <Route path="/distributor/dashboard" element={<DistributorDashboard />} />
          <Route path="/distributor/orders" element={<DistributorOrders />} />
          <Route path="/distributor/primary-orders" element={<DistributorPrimaryOrders />} />
          <Route path="/distributor/retailers" element={<DistributorRetailers />} />
        </Route>

        <Route
          element={(
            <Protected>
              <TenantOnly>
                <AppShell />
              </TenantOnly>
            </Protected>
          )}
        >
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/areas" element={<AreasPage />} />
          <Route path="/customers/form" element={<FormBuilderPage />} />
          <Route path="/visits" element={<VisitsPage />} />
          <Route path="/visits/plans" element={<JourneyPlansPage />} />
          <Route path="/visits/schedule-report" element={<ScheduleReportPage />} />
          <Route path="/visits/form" element={<FormBuilderPage kind="visit" />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/products" element={<ProductsPage />} />
          <Route path="/orders/masters" element={<ProductMastersPage />} />
          <Route path="/orders/price-lists" element={<PriceListsPage />} />
          <Route path="/reports/dashboard" element={<VisitDashboardPage />} />
          <Route path="/reports/analytics" element={<VisitAnalyticsPage />} />
          <Route path="/reports/visits" element={<VisitReportPage />} />
          <Route path="/reports/orders" element={<OrderReportPage />} />
          <Route path="/reports/order-lines" element={<OrderLinesReportPage />} />
          <Route path="/reports/rep/:userId" element={<RepDashboardPage />} />
          <Route path="/reports/ask" element={<AssistantPage />} />
          <Route path="/leave/requests" element={<LeaveRequestsPage />} />
          <Route path="/leave/types" element={<LeaveTypesPage />} />
          <Route path="/leave/calendar" element={<CalendarPage />} />
          <Route path="/tracking" element={<TrackingLivePage />} />
          <Route path="/tracking/replay" element={<RouteReplayPage />} />
          <Route path="/expenses" element={<ExpenseDashboardPage />} />
          <Route path="/expenses/claims" element={<ExpenseClaimsPage />} />
          <Route path="/expenses/categories" element={<ExpenseCategoriesPage />} />
          <Route path="/dispatch" element={<DispatchSheetsPage />} />
          <Route path="/dispatch/reasons" element={<DispatchReasonsPage />} />
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/leads/dashboard" element={<LeadDashboardPage />} />
          <Route path="/leads/pipeline" element={<LeadPipelinePage />} />
          <Route path="/leads/form" element={<FormBuilderPage kind="lead" />} />
          <Route path="/distributors" element={<DistributorsPage />} />
          <Route path="/distributors/areas" element={<DistributorAreasPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>

        <Route path="*" element={<HomeRedirect />} />
      </Routes>
    </ToastProvider>
  );
}
